import { Preferences } from '@capacitor/preferences';
import { QueryClient, useQuery } from '@tanstack/react-query';
import Keys from 'preferences/preferences';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { selectCurrentUser } from 'store/users/selectors';

/** A message the server never received, kept on the device until it is sent again or dropped. */
export type FailedMessage = ConversationMessage & { conversationId: string };

/**
 * In storage rather than in the session's state: a network error logs the
 * patient out (see AuthContext), and once she has logged back in and returns
 * to the conversation, her messages are still there to send again.
 */
const failedMessagesQueryKey = ['failedMessages'] as const;

async function loadFailedMessages(): Promise<FailedMessage[]> {
  try {
    const { value } = await Preferences.get({ key: Keys.FAILED_MESSAGES });
    return value ? JSON.parse(value) : [];
  } catch (error) {
    console.error('Failed to load unsent messages from storage:', error);
    return [];
  }
}

// One update at a time, so that two failures in a row both get recorded
let pendingUpdate: Promise<unknown> = Promise.resolve();

function updateFailedMessages(queryClient: QueryClient, update: (messages: FailedMessage[]) => FailedMessage[]) {
  pendingUpdate = pendingUpdate.then(async () => {
    const messages = update(await loadFailedMessages());
    await Preferences.set({ key: Keys.FAILED_MESSAGES, value: JSON.stringify(messages) }).catch(error =>
      console.error('Failed to save unsent messages to storage:', error),
    );
    queryClient.setQueryData(failedMessagesQueryKey, messages);
  });
  return pendingUpdate;
}

export function addFailedMessage(queryClient: QueryClient, message: FailedMessage) {
  const failed = { ...message, pending: undefined, failed: true };
  return updateFailedMessages(queryClient, messages => [...messages, failed]);
}

export function removeFailedMessage(queryClient: QueryClient, messageId: number) {
  return updateFailedMessages(queryClient, messages => messages.filter(m => m.id !== messageId));
}

/** The current user's messages that could not be sent in this conversation, oldest first. */
export default function useFailedMessages(conversationId?: string): ConversationMessage[] {
  const currentUser = useSelector(selectCurrentUser);
  const { data } = useQuery({
    queryKey: failedMessagesQueryKey,
    queryFn: loadFailedMessages,
    staleTime: Infinity,
  });
  return useMemo(
    () =>
      (data ?? []).filter(
        message => message.conversationId === conversationId && message.sender?.id === currentUser?.id,
      ),
    [data, conversationId, currentUser?.id],
  );
}
